import { t } from '@framework/i18n/intl/i18next.ts';
import { yup } from '@framework/validation/initYup.ts';
import { isYupArraySchema } from '@framework/validation/YupArraySchema.ts';
import { isYupBooleanSchema } from '@framework/validation/YupBooleanSchema.ts';
import { FormBuilderMetaFlags } from '../../../../validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { RuleType } from '../../IRule.ts';
import { type IRuleType } from '../../IRuleType.ts';
import { RequiredRuleComponent } from './RequiredRuleComponent.tsx';

const configValidator = yup
  .object({
    value: yup.boolean().notRequired().label('Campo Richiesto'),
    customMessage: yup
      .string()
      .notRequired()
      .label('Messaggio personalizzato di validazione'),
  })
  .notRequired()
  .nullable()
  .noUnknown();

type IRuleRequiredType = yup.InferType<typeof configValidator>;

export const RequiredRule: IRuleType<IRuleRequiredType> = {
  type: RuleType.Required,
  name: 'Campo Richiesto',
  configComponent: RequiredRuleComponent,
  configValidator,
  validator: (schema, rule) => {
    if (!rule?.value) return schema;

    const flags = schema.meta?.() ?? {};

    // Se è attivo il flag "Ignora required", non applico la regola
    if (flags[FormBuilderMetaFlags.IgnoreRequired]) {
      return schema;
    }

    // Disabilitiamo il controllo lint in quento dobbiamo controllare la stringa vuota ('')
    const customMessage = rule.customMessage ? rule.customMessage : undefined;

    // Per gli array, il required equivale a minimo 1 elemento
    if (isYupArraySchema(schema)) {
      return schema.min(1, customMessage).required();
    }

    if (isYupBooleanSchema(schema)) {
      return schema
        .oneOf(
          [true],
          customMessage ??
            t(
              'required-rule.boolean-message',
              'Il campo ${label} è un campo richiesto',
            ),
        )
        .required();
    }

    return schema.required(customMessage);
  },
};

export function isRequiredRuleTrue(
  rules: Record<string, unknown> | undefined,
): boolean {
  if (!rules) return false;
  const requiredRule = rules[RuleType.Required] as
    | IRuleRequiredType
    | undefined;

  if (!requiredRule) return false;
  return !!requiredRule.value;
}
